import React from 'react';
import PropTypes from 'prop-types';
import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import './style.scss';

@observer
export default class Slider extends React.Component {

	@observable sliding = false;
	@observable curSlide = 0;
	@observable num = 0;

	static propTypes = {

		/**
		 * Высота слайдера (default: 'auto')
		 * auto - в зависимости от высоты текущего слайда
		 * max - высота самого высокого слайда
		 * число в пикселях
		 */
		height: PropTypes.oneOfType([PropTypes.oneOf(['auto', 'max']), PropTypes.number]),

		/** Номер слайда, с которого начинать показ (default: 0) */
		start: PropTypes.number,

		/** Автоматическое слайдшоу (default: false) */
		autoPlay: PropTypes.bool,

		/** Время, которое слайд висит на экране (при autoPlay), в ms (default: 5000) */
		interval: PropTypes.number,

		/** задержка перед слайд-шоу (при autoPlay), в ms (default: 1000) */
		delay: PropTypes.number,

		/** длительность перехода между слайдами (при autoPlay), в ms (default: 500) */
		transition: PropTypes.number,

		/** callback(N), перед входом слайда N */
		onSlideWillEnter: PropTypes.func,

		/** callback(N), после входа слайда N */
		onSlideDidEnter: PropTypes.func,

		/** callback(N), перед уходом слайда N */
		onSlideWillLeave: PropTypes.func,

		/** callback(N), после ухода слайда N */
		onSlideDidLeave: PropTypes.func,

		/** Класс для контейнера слайдов (default: '') */
		className: PropTypes.string,

		/** Выключает анимацию (default: false) */
		noAnimation: PropTypes.bool,

		/** Навигация в виде стрелок */
		arrows: PropTypes.bool,

		/** Навигация в виде точек внизу */
		dots: PropTypes.bool,

		dotRender: PropTypes.func,
	};

	static defaultProps = {
		start: 0,
		autoPlay: false,
		className: '',
		noAnimation: false,
		interval: 5000,
		delay: 1000,
		transition: 500,
		height: 'auto',
		arrows: true,
		dots: false,
		dotRender: () => <span />,
	};

	constructor(props) {
		super(props);
		this.childrenNodes = [];
	}

	componentDidMount() {
		this.num = React.Children.count(this.props.children);
		// console.log('num', this.num);
		document.addEventListener('resize', this.onResize);

		this.childrenNodes.forEach((slide, i) => {
			const observer = new MutationObserver(this.onMutation);
			observer.observe(slide, { childList: true, characterData: true, subtree: true });
		});

		this.setup();
		this.setHeight();
		this.startSlideshow();
		if (this.props.start > 0) this.gotoSlide(this.props.start, true);
	}

	onMutation = () => {
		this.setHeight();
	};

	componentDidUpdate() {
		this.setup();
		this.setHeight();
	}

	componentWillUnmount() {
		document.removeEventListener('resize', this.onResize);
	}

	setup() {
		if (this.container) this.width = this.container.offsetWidth;
	}

	setHeight() {
		if (this.childrenNodes.length === 0) return;
		if (!this.container) return;

		if (this.props.height === 'auto') {
			const height = this.childrenNodes[this.curSlide].offsetHeight;
			if (height > 0) {
				this.container.style.height = 'auto';
				this.container.style.maxHeight = height + 'px';
			}
		}
		else if (this.props.height === 'max') {
			const height = Math.max(...this.childrenNodes.map(e => e.offsetHeight));
			if (height > 0) {
				this.container.style.height = 'auto';
				this.container.style.maxHeight = height + 'px';
			}
		}
		else if (typeof this.props.height === 'number') {
			this.container.style.height = this.props.height + 'px';
			this.container.style.maxHeight = this.props.height + 'px';
		}
	}

	onResize = () => {
		this.width = this.container.offsetWidth;
		if (this.well) {
			this.well.style.transition = `none`;
			this.well.style.transform = `translate(-${this.curSlide * this.width}px)`;
		}
	};

	startSlideshow() {
		if (this.props.autoPlay) {
			clearInterval(this.timer);
			clearInterval(this.delay);
			this.delay = setTimeout(() => {
				this.timer = setInterval(this.next, this.props.interval);
			}, this.props.delay);
		}
	}

	leaveData(dir) {
		return {
			transition: `transform ${this.props.transition}ms, opacity ${this.props.transition/2}ms`,
			transform: `scale(0.75,0.75) translate3d(${dir * this.width * 3 / 4}px, 0, -1em)`,
			opacity: 0,
		}
	}

	next = (noAnimation = false) => {
		let nextSlide = this.curSlide + 1;
		if (nextSlide > this.num - 1) {
			nextSlide = 0;
			this.gotoSlide(nextSlide, noAnimation, 1);
		}
		else {
			this.gotoSlide(nextSlide, noAnimation);
		}
		this.startSlideshow();
	};

	prev = (noAnimation = false) => {
		let prevSlide = this.curSlide - 1;
		if (prevSlide < 0) {
			prevSlide = this.num - 1;
			this.gotoSlide(prevSlide, noAnimation, -1);
		}
		else {
			this.gotoSlide(prevSlide, noAnimation);
		}
		this.startSlideshow();
	};

	dotClick = (e, i) => {
		e.preventDefault();
		this.gotoSlide(i)
	};

	gotoSlide(i, noAnimation, forceDir) {
		if (this.sliding) return;

		this.sliding = true;

		const curSlide = this.curSlide;
		const delta = Math.abs(this.curSlide - i);
		const dir = forceDir || (i < curSlide ? -1 : 1);

		const next = curSlide + dir;
		const shift = next - i;
		const curNode = this.childrenNodes[curSlide];
		const nextNode = next < this.num ? this.childrenNodes[next] : null;
		const targetNode = this.childrenNodes[i];

		// console.log('gotoSlide:', i, ', width:', this.width, ', curSlide:', curSlide, ', next:', next, ', delta:', delta);
		// console.log('forceDir', forceDir, ', nextNode', nextNode, ', shift:', shift);

		this.curSlide = i;

		if (this.props.onSlideWillLeave) this.props.onSlideWillLeave(curSlide, curNode);
		if (this.props.onSlideWillEnter) this.props.onSlideWillEnter(i, targetNode);

		this.setHeight();

		if (this.props.noAnimation || noAnimation) {
			if (this.well) {
				this.well.style.transition = `none`;
				this.well.style.transform = `translate3d(${-i * this.width}px, 0, 0)`;
			}
			this.sliding = false;
			return;
		}

		if (this.well) this.well.style.transition = `transform ${this.props.transition}ms`;

		const leave = this.leaveData(dir);
		if (curNode) {
			curNode.style.transition = leave.transition;
			curNode.style.transform = leave.transform;
			curNode.style.opacity = leave.opacity;
		}

		if (delta > 1 || (this.num === 2 && ((curSlide === 1 && forceDir === 1) || (curSlide === 0 && forceDir === -1)))) {
			targetNode.style.transform = `translate3d(${shift * this.width}px, 0, 0)`;
			if (nextNode) nextNode.style.opacity = 0;
		}

		if (this.well) this.well.style.transform = `translate3d(${- next * this.width}px, 0, 0)`;
		// console.log('well:', next, -next * this.width, this.well.style.transform);

		const _this = this;
		setTimeout(() => {
			if (delta > 1 || (this.num === 2 && ((curSlide === 1 && forceDir === 1) || (curSlide === 0 && forceDir === -1)))) {
				targetNode.style.transform = `none`;
				if (nextNode) nextNode.style.opacity = 1;
				if (_this.well) {
					_this.well.style.transition = `none`;
					_this.well.style.transform = `translate3d(${-i * _this.width}px, 0, 0)`;
				}
			}
			if (curNode) {
				curNode.style.transition = `none`;
				curNode.style.transform = `none`;
				curNode.style.opacity = 1;
			}
			this.sliding = false;

			if (this.props.onSlideDidEnter) this.props.onSlideDidEnter(i, targetNode);
			if (this.props.onSlideDidLeave) this.props.onSlideDidLeave(curSlide, curNode);

		}, this.props.transition + 50);

	}

	render() {
		const { arrows, dots, dotRender, className } = this.props;

		const children = React.Children.map(this.props.children, (child, i) => (
			<div className={`ui-slide ui-slide-${i}`}
			     ref={e => {if (e) this.childrenNodes[i] = e;}}>{child}</div>
		));

		const disableArrows = React.Children.count(this.props.children) === 1 || !arrows;
		// console.log('disableArrows', disableArrows, React.Children.count(this.props.children));

		const height = parseInt(this.props.height);

		return <div className="ui-slider">
			{!disableArrows && <FontAwesomeIcon icon={faChevronLeft} className="ui-slider-prev" onClick={() => this.prev(false)} />}
			<div className={"ui-slider-container " + className}
			     style={isNaN(height) ? {} : { maxHeight: height + 'px'}}
			     ref={e => this.container = e}>
				<div className="ui-slider-well" ref={e => this.well = e}>
					{children}
				</div>
			</div>
			{!disableArrows && <FontAwesomeIcon icon={faChevronRight} className="ui-slider-next" onClick={() => this.next(false)} />}
			{dots && <div className="ui-slider-dots-nav">
				{[...new Array(this.num)].map((v, i) => (
					<a key={i} href="#" className={i === this.curSlide ? 'current' : ''} onClick={e => this.dotClick(e, i)}>
						{dotRender(i + 1, i === this.curSlide)}
					</a>
				))}
			</div>}
		</div>;
	}

}
